import type { DraftState, WizardSampleGroup } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { planSheetApply } from './sheet/applyPlan.ts'
import { archiveConversions, planArchiveApply } from './archiveApply.ts'
import { placedGroups, removablePools } from './pools.ts'
import { pendingRemovals, removalHolding, removalHoldingGroup } from './removal.ts'
import { missingReverse } from './reverseFiles.ts'

/*
 * One predicate per write step, shared by the step that makes the write (OrderWizardWrites.ts) and by
 * isStillWriting, so review never waits for a write the queue will not make.
 */

/** What went wrong writing a group into the order, while nothing has written it since. */
export const groupWriteError = (group: WizardSampleGroup): string | undefined =>
  group.item.error ??
  (group.item.status === 'failed' ? 'the item could not be created' : undefined) ??
  (group.reverseAttach?.status === 'failed' ? (group.reverseAttach.error ?? 'the R2 file could not be added') : undefined) ??
  (group.applied?.status === 'failed' ? (group.applied.error ?? 'the sheet data could not be written') : undefined)

/** A created item outside the pool its group is assigned to, to be moved there. */
export const dueForMove = (draft: DraftState, group: WizardSampleGroup): boolean =>
  group.item.status === 'created' &&
  !!group.item.itemId &&
  !group.item.error &&
  !!draft.poolIds[0] &&
  (group.item.poolId ?? draft.poolIds[0]) !== draft.poolIds[group.pool ?? 0]

/** A created item whose label changed since, to be converted. */
export const dueForConversion = (group: WizardSampleGroup): boolean =>
  group.item.status === 'created' &&
  !!group.item.itemId &&
  !!group.item.itemType &&
  !!group.itemType &&
  group.item.itemType !== group.itemType &&
  !group.item.error

/** A created item whose R2 joined it afterwards, uploaded and waiting to be attached; not before a format switch is written. */
export const dueForAttach = (draft: DraftState, group: WizardSampleGroup): boolean =>
  group.reverseAttach?.status === 'pending' &&
  group.item.status === 'created' &&
  !!group.item.itemId &&
  !!group.reverse &&
  !!draft.files[group.reverse]?.upload?.serverFileId &&
  !draft.settingsWrite?.sampleConfiguration &&
  !removalHolding(draft, [draft.files[group.reverse]?.upload?.serverFileId])

/** Archive items whose sample's label differs from the server's type, to be converted. */
export const archiveConversionsDue = (draft: DraftState) => archiveConversions(draft).filter(({ group }) => !group.item.error)

/** Settings the lab changed that the order does not hold yet. */
export const settingsWriteDue = (draft: DraftState): boolean => !!draft.settings && draft.settingsWrite?.status === 'pending'

/* a failed write is tried again with the next pass, but review need not wait for it: it shows the failure */
const notFailedBefore = (entry: { group: WizardSampleGroup }) => entry.group.applied?.status !== 'failed'

/**
 * Whether the wizard still owes the order a write: review and submit read the order as the server holds
 * it, so they wait until nothing is left. `filesAtHand` says whether a group's files can still be read,
 * which decides whether a missing primer estimate is still coming.
 */
export const isStillWriting = (draft: DraftState, filesAtHand: (group: WizardSampleGroup) => boolean): boolean => {
  const groups = Object.values(draft.groups)
  const primersFound = draft.discovery?.primers.status === 'found'
  return (
    settingsWriteDue(draft) ||
    groups.some((group) => group.item.status === 'creating' || group.applied?.status === 'pending') ||
    pendingRemovals(draft).length > 0 ||
    groups.some((group) => dueForConversion(group) || dueForMove(draft, group) || dueForAttach(draft, group)) ||
    (primersFound && groups.some((group) => !group.estimate && filesAtHand(group))) ||
    planSheetApply(draft).some(notFailedBefore) ||
    planArchiveApply(draft).some(notFailedBefore) ||
    archiveConversionsDue(draft).length > 0 ||
    removablePools(draft).length > 0
  )
}

/**
 * Whether the order on the server now shows what the draft does, so the submit checks can be run
 * against it: every item created, nothing left to write, no primer gap the wizard still asks about.
 */
export const submitChecksDue = (draft: DraftState, filesAtHand: (group: WizardSampleGroup) => boolean): boolean => {
  const groups = placedGroups(draft)
  return (
    groups.length > 0 &&
    groups.every((group) => group.item.status === 'created') &&
    !isStillWriting(draft, filesAtHand) &&
    !draft.questions.some((question) => question.kind === 'primers-missing')
  )
}

export type UploadSummary = {
  uploaded: number
  /** Uploading now or queued. */
  uploading: number
  failed: number
  needsReadd: number
  /** Every file that is to be uploaded: the four above. */
  total: number
  itemsCreated: number
  itemsTotal: number
}

/** Where the draft's uploads and items stand, as the header, the Files step and the page count them. */
export const uploadSummary = (draft: DraftState): UploadSummary => {
  const files = Object.values(draft.files)
  const status = (...statuses: string[]) => files.filter((file) => !!file.upload && statuses.includes(file.upload.status)).length
  const summary = {
    uploaded: status('uploaded'),
    uploading: status('uploading', 'pending'),
    failed: status('failed'),
    needsReadd: status('needs-readd')
  }
  const groups = placedGroups(draft)
  return {
    ...summary,
    total: summary.uploaded + summary.uploading + summary.failed + summary.needsReadd,
    itemsCreated: groups.filter((group) => group.item.status === 'created').length,
    itemsTotal: groups.length
  }
}

const UPLOAD_STUCK = new Set(['failed', 'needs-readd'])

/* a waiting item whose file failed or has to be dropped again, or whose old item could not be removed, waits for the lab */
const waitsForTheLab = (draft: DraftState, group: WizardSampleGroup) =>
  !draft.poolIds[0] ||
  missingReverse(draft, group) ||
  removalHoldingGroup(draft, group)?.status === 'failed' ||
  [group.forward, group.reverse].some((key) => {
    const upload = key ? draft.files[key]?.upload : undefined
    return !!key && (!upload || UPLOAD_STUCK.has(upload.status))
  })

/**
 * Whether files are still uploading or the wizard still owes the order a write, which the header shows
 * as a spinner and review waits for. A failed upload, item or write is not: it is shown as a failure.
 */
export const isBusy = (draft: DraftState, filesAtHand: (group: WizardSampleGroup) => boolean): boolean =>
  Object.values(draft.files).some((file) => file.upload?.status === 'uploading' || file.upload?.status === 'pending') ||
  Object.values(draft.groups).some(
    (group) => group.item.status === 'creating' || (group.item.status === 'waiting' && !waitsForTheLab(draft, group))
  ) ||
  isStillWriting(draft, filesAtHand)
